import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { toast } from 'react-toastify';
import OtpInput from './OtpInput';

const MobileVerificationSection = ({ 
  onVerificationSuccess,
  isGuest = true,
  className = "",
  otpLength = 6
}) => {
  const [verifyMobile, setVerifyMobile] = useState('');
  const [verifyCountryCode, setVerifyCountryCode] = useState('+91');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [isProcessingOtp, setIsProcessingOtp] = useState(false);
  const [canSendOtp, setCanSendOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Check if user exists when mobile number changes
  useEffect(() => {
    const checkExistingUser = async () => {
      if (verifyMobile.length >= 10) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/users/check-exists`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                type: 'mobile',
                value: verifyMobile.replace(/[^0-9]/g, '').slice(-10),
              }),
            }
          );
          const data = await response.json();
          const exists = data.success && data.responseObject.exists;
          setIsNewUser(!exists);
          setCanSendOtp(exists);

          // Update parent component with mobile number changes
          onVerificationSuccess({
            mobile: verifyMobile.replace(/[^0-9]/g, '').slice(-10),
            countryCode: verifyCountryCode
          });
        } catch (error) {
          console.error('Error checking user:', error);
          toast.error("Failed to verify mobile number");
          setCanSendOtp(false);
          setIsNewUser(false);
        }
      } else {
        setCanSendOtp(false);
        setIsNewUser(false);
        if (verifyMobile.length === 0) {
          onVerificationSuccess({
            mobile: '',
            countryCode: verifyCountryCode
          });
        }
      }
    };

    checkExistingUser();
  }, [verifyMobile, verifyCountryCode]);

  const handleSendOtp = async () => {
    setIsProcessingOtp(true);
    try {
      if (!verifyMobile || verifyMobile.length < 10) {
        toast.error("Please enter a valid mobile number");
        return;
      }

      const cleanMobile = verifyMobile.replace(/[^0-9]/g, '');
      const phoneNumber = `${verifyCountryCode}${cleanMobile.slice(-10)}`;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/send-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mobile: phoneNumber
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setShowOtpInput(true);
        toast.success("OTP sent successfully");
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP");
    } finally {
      setIsProcessingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsVerifyingOtp(true);
    try {
      const cleanMobile = verifyMobile.replace(/[^0-9]/g, '');
      const phoneNumber = `${verifyCountryCode}${cleanMobile.slice(-10)}`;

      // First verify the OTP
      const verifyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/verify-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mobile: phoneNumber,
            otp: otp
          }),
        }
      );

      const verifyData = await verifyResponse.json();
      
      if (!verifyData.success) {
        toast.error(verifyData.message || "Invalid OTP");
        return;
      }

      // If OTP verification successful, get user details
      const userResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/verify-and-get`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'mobile',
            value: cleanMobile.slice(-10)
          }),
        }
      );

      const userData = await userResponse.json();
      if (userData.success) {
        onVerificationSuccess(userData.responseObject);
        toast.success("Mobile number verified successfully");
        setShowOtpInput(false);
      } else {
        toast.error(userData.message || "Failed to get user details");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Verification failed");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const getButtonText = () => {
    if (isProcessingOtp) {
      return 'Sending...';
    }
    if (isNewUser) {
      return '';
    }
    if (!canSendOtp) {
      return '';
    }
    return 'Send OTP';
  };

  const validateInput = () => {
    if (verifyMobile.length !== 10) {
      return 'Enter valid details';
    }
    return '';
  };

  return (
    <div className={` shadow-sm border-0 ${className}`}>
      <div className="w-100">
        <div className="w-100">
          <label className="form-label">Mobile Number</label>
          <div className="input-group">
            <PhoneInput
              country="in"
              value={`${verifyCountryCode}${verifyMobile}`}
              onChange={(phone, data) => {
                const dialCode = data.dialCode;
                const phoneWithoutCode = phone.slice(dialCode.length);
                setVerifyMobile(phoneWithoutCode);
                setVerifyCountryCode("+" + dialCode);
              }}
              inputClass={`form-control ${validateInput() ? 'is-invalid' : ''}`}
              containerClass="phone-input-container"
              containerStyle={{ width: '100%', flex: 1 }}
              inputStyle={{ width: '100%', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              specialLabel=""
              countryCodeEditable={false}
            />
            {getButtonText() && (
                <button 
                  className="thm-btn"
                  onClick={handleSendOtp}
                  disabled={!canSendOtp || isProcessingOtp || isNewUser}
                  style={{ 
                    whiteSpace: 'nowrap', 
                    minWidth: '150px',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                >
                  {getButtonText()}
                </button>
            )}
          </div>
          {validateInput() && <div className="invalid-feedback d-block">Please enter a valid 10-digit mobile number</div>}
        </div>

        {showOtpInput && (
          <div className="mt-3 text-center">
            <label className="form-label">Enter OTP</label>
            <OtpInput
              length={otpLength}
              onOtpSubmit={(value) => setOtp(value)}
            />
            <button
              className="thm-btn mt-3 mb-3 w-50"
              onClick={handleVerifyOtp}
              disabled={isVerifyingOtp || otp.length !== otpLength}
            >
              {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileVerificationSection; 